import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    Switch, TextField, Typography
} from "@mui/material";
import {Done} from "~/ui/icons";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {Controller, useForm} from "react-hook-form";
import {DateField} from "~/apps/core/ui/DateField";
import moment from "moment/moment";
import {VehicleSelect} from "~/apps/packages/auto-visits/components/AutoVisitForm/VehicleSelect";
import {new2oldDriver, new2oldVehicle, old2newVehicle} from "~/apps/packages/auto-visits/queries";
import {ContractSelect} from "~/apps/packages/auto-shipment-orders/components/CreateOrderForm/ContractSelect";
import {CarrierSelect} from "~/apps/packages/auto-shipment-orders/components/CreateOrderForm/CarrierSelect";
import {makeStyles} from "@mui/styles";
import {PortTheme} from "~/ui/theme";
import {EditableCargoList} from "~/apps/packages/auto-visits/components/AutoVisitForm/EditableCargoList";
import {OrderItem} from "~/apps/packages/auto-shipment-orders/queries";
import {CargoList} from "~/apps/packages/auto-shipment-orders/components/CreateOrderForm/CargoList";

type FormValues = {
    date: Date,
    contract: string,
    exportByConsignee: boolean,
    carrier?: string
    consignee?: string
    consigneeDocument?: string
    cargoList?: OrderItem['cargos']
}

const FormCard = (props) => (
    <Card>
        <CardContent>
            <Grid container direction="column" spacing={2}>
                {React.Children.map(props.children, (item, index) => (
                    <Grid item key={index}>
                        {item}
                    </Grid>
                ))}
            </Grid>
        </CardContent>
    </Card>
)

const useStyles = makeStyles<PortTheme, { exportByConsignee: boolean }>((theme) => ({
    switchLabelRoot: {
        marginLeft: 0,
    },
    switchLabel: {
        fontSize: '16px!important',
        fontWeight: '700!important',
        lineHeight: '25px!important',
        color: '#18395B!important'
    },
    ul: {
        padding: '0',
        margin: 0,
        listStyle: 'none'
    },
    li: {
        position: 'relative',
        color: props => props.exportByConsignee ? '#18395B' : '#8797A9',
        transition: '.2s ease',
        display: "block",
        paddingLeft: '16px',
        '&:before': {
            content: '""',
            transition: '.2s ease',
            width: 3,
            height: 3,
            background: props => props.exportByConsignee ? '#18395B' : '#8797A9',
            position: 'absolute',
            borderRadius: 3,
            top: '9px',
            left: 7,
        }
    },
    disabled: {
        borderRadius: '2px',
        background: 'rgba(63, 121, 181, 0.12)',
    }
}))

type CreateOrderFormProps = {
    carrierId: string
}

export const CreateOrderForm = (props: CreateOrderFormProps) => {
    const { carrierId } = props;
    const {handleSubmit, getValues, watch, control, errors, formState, setValue} = useForm<FormValues>({
        defaultValues: {
            date: moment().toDate(),
            cargoList: [],
            contract: null,
            exportByConsignee: false,
            carrier: null,
            consignee: null,
            consigneeDocument: null
        }
    });

    const exportByConsignee = watch('exportByConsignee');

    const styles = useStyles({exportByConsignee});
    return (
        <Box m={2}>
           <form>
               <Grid container direction="column" rowSpacing={2}>
                   <Grid item>
                       <FormCard>
                            <Controller
                                name="date"
                                control={control}
                                rules={{ required: true }}
                                render={({onChange, value}) => (
                                    <DateField
                                        label="Дата визита"
                                        fullWidth
                                        value={value ? moment(value) : value}
                                        onChange={value => {
                                            onChange(value);
                                        }}
                                    />
                                )}
                            />
                           <Controller
                               name="contract"
                               control={control}
                               rules={{required: true}}
                               render={({onChange, value}) => (
                                   <ContractSelect
                                       label="Договор"
                                       fullWidth
                                       value={value}
                                       onChange={onChange}
                                       //disabled={disabled}
                                   />
                               )}
                           />
                       </FormCard>
                   </Grid>
                   <Grid item padding={0}>
                       <FormCard>
                           <Controller
                               name="exportByConsignee"
                               control={control}
                               rules={{required: true}}
                               render={({onChange, value}) => (
                                   <FormControl>
                                       <FormControlLabel
                                           //disabled={disabled}
                                           control={
                                               <Switch checked={value} onChange={(e) => onChange(e.target.checked)} />
                                           }
                                           label="Вывоз грузополучателем"
                                           classes={{root: styles.switchLabelRoot, label: styles.switchLabel}}
                                       />
                                   </FormControl>
                               )}
                           />
                           <ul className={styles.ul}>
                               <Typography component="li" variant="screenSubtitle" classes={{root: styles.li}} marginBottom={'10px'}>
                                   Самоходный груз/ ТС грузоподъемностью до&nbsp;6т&nbsp;(кроме седельных тягачей).
                               </Typography>
                               <Typography component="li" variant="screenSubtitle" classes={{root: styles.li}} className={styles.disabled}>
                                   Допустимы к перевозке только генеральные&nbsp;и&nbsp;насыпные грузы.
                               </Typography>
                           </ul>
                       </FormCard>
                   </Grid>
                   {exportByConsignee
                       ? <Grid item>
                           <FormCard>
                               <Controller
                                   name="consignee"
                                   control={control}
                                   rules={{required: true}}
                                   render={({onChange, value}) => (
                                       <TextField
                                           label="Грузополучатель"
                                           placeholder="Введите текст"
                                           fullWidth
                                           value={value}
                                           onChange={onChange}
                                       />
                                   )}
                               />
                               <Controller
                                   name="consigneeDocument"
                                   control={control}
                                   rules={{required: true}}
                                   render={({onChange, value}) => (
                                       <TextField
                                           label="Документ грузополучателя"
                                           placeholder="Введите текст"
                                           fullWidth
                                           value={value}
                                           onChange={onChange}
                                       />
                                   )}
                               />
                           </FormCard>
                       </Grid>
                       : <Grid item>
                           <FormCard>
                               <Controller
                                   name="carrier"
                                   control={control}
                                   rules={{required: true}}
                                   render={({onChange, value}) => (
                                       <CarrierSelect
                                           label="Договор"
                                           fullWidth
                                           value={value}
                                           onChange={onChange}
                                           //disabled={isSubmitting}
                                       />
                                   )}
                               />
                           </FormCard>
                       </Grid>
                   }
                   <Grid item>
                       <Typography variant="screenText">
                           Выбор грузов
                       </Typography>
                   </Grid>
                   <Grid item>
                       <Controller
                           name="cargoList"
                           control={control}
                           rules={{
                               required: true,
                               validate: value => {
                                   if (!value.length) {
                                       return "Не выбран груз";
                                   }
                               }
                           }}
                           render={({onChange: onChangeCargoList, value: cargoList}) => (
                               <CargoList
                                   value={cargoList.map(cargoItem => {
                                       return {
                                           ...cargoItem,
                                           cargoId: cargoItem.id,
                                       };
                                   })}
                                   onChange={value => {
                                       const cargoList = [];
                                       for (const cargo of value) {
                                           cargoList.push({
                                               ...cargo,
                                           });
                                       }
                                       onChangeCargoList(cargoList);
                                   }}
                                   //disabled={isSubmitting}
                               />
                           )}
                       />
                   </Grid>
               </Grid>
               <Box height="80px"/>
               <FloatingActions>
                   <Button
                       type="submit"
                       fullWidth
                       color="primary"
                       //disabled={submitDisabled}
                       startIcon={<Done/>}
                   >
                       Создать наряд на авто
                   </Button>
               </FloatingActions>
           </form>
        </Box>
    )
}